// The Figma surface inside Focus: one connect card, one chip per link.
//
// Placement is the point. Nothing here is a toast — Focus's transcript is the
// durable surface, the chips are folded from the replayed `figma_link_state`
// events, and every state therefore survives a reconnect and a reload without
// this component holding any of it. The local state in the file is ephemeral by
// design: which declined chips the user has re-expanded (collapsed is the safe
// default, and the DECLINE itself is server-held), the connect outcome read off
// the URL before the marker is stripped, and how long an allow button stays
// disabled after a click or a refusal (a remount re-disables it, which is the
// direction that can never offer a click the server would throttle).
//
// The chip is a REPRESENTATION, never an authority (the rule `BreakdownCard`'s
// header states). It renders what the server said; it does not decide whether a
// design was read, and it cannot ground anything — a fetched design can never
// ground a readiness slot, which is a server-side invariant this file is
// downstream of, not a rule it enforces.

import { useCallback, useEffect, useMemo, useState } from "react";

import { useLocale, useT } from "../../i18n/index.js";
import { useFormatNumber, useRelativeTime } from "../../i18n/format.js";
import type { FigmaLinkSnapshot } from "../../chat/useWsSession";

import { useHereLocation } from "./useHereLocation";
import { formatFigmaResetInstant, useFigmaLimitLine } from "./limitCopy";
import { FigmaPreview } from "./FigmaPreview";
import { PREVIEW_CHAT_MAX_H, type DesignScene } from "./useDesignScene";

import {
  figmaChipCopy,
  figmaChipTitle,
  figmaConnectHref,
  figmaConnectPrompt,
  figmaRefreshCopy,
  figmaResultCopyKey,
  figmaReturnToPath,
  FIGMA_ALLOW_RETRY_MS,
  FIGMA_RESULT_PARAM,
  type FigmaChipCopy,
} from "./figmaCopy";

export interface FigmaLinkPanelProps {
  /** `useWsSession.figmaLinks` — already folded, already deduped by linkId. */
  readonly links: ReadonlyArray<FigmaLinkSnapshot>;
  /**
   * Whether a `read` chip may offer "Show the sketch".
   *
   * DEFAULT FALSE, and opting in is per call site rather than per surface,
   * because this panel has THREE of them and only two are chat. The third
   * (`RefineFlow`) renders under the step body on every conversational screen —
   * so a toggle here would put a 400-520px drawing directly beneath whatever
   * question is on screen, and on the sketch question itself the same drawing
   * would be reachable twice. One question per screen is the rule the flow
   * enforces twice already; the widget must not break it through its other mode.
   */
  readonly allowPreview?: boolean;
  /** The socket's session — the fallback sketch is a credentialed GET.
   *  Only read when `allowPreview` is on. */
  readonly sessionId?: string | null;
  /**
   * `ws.figmaSceneFor` — the picture THIS chat had for a chip's `linkId`, or
   * `null` to ask the route (which serves Loom's newest copy of the frame).
   * Only read when `allowPreview` is on.
   *
   * OPTIONAL because the flow's own panel keeps `allowPreview` off and has no
   * sketch to hand anyone; a chat panel that omits it silently falls back to the
   * route, which is the pre-`figma_scene` behaviour and never a broken screen.
   */
  readonly sceneFor?: (handle: string) => DesignScene | null;
  /** `ws.sendFigmaConnectDeclined`. */
  readonly onDecline: (linkId: string, scope: "link" | "session") => void;
  /** `ws.sendFigmaAllowFile`. */
  readonly onAllowFile: (linkId: string) => void;
  /**
   * Every link in the conversation, when `links` is only the ones ONE message
   * carried (a chat renders one panel under each message). The connect card is
   * decided over all of them — one card per chat, never one per message, and
   * the "don't ask again" offer still counts a decline under another message —
   * and it renders only in the panel that holds the link it is about (for a
   * link pasted in several messages, the newest of them). Absent,
   * `links` is the whole conversation, as it was before panels were split.
   */
  readonly allLinks?: ReadonlyArray<FigmaLinkSnapshot>;
  /**
   * Test seam. Production follows the router's history (see
   * `useHereLocation`); a test hands the two halves in rather than
   * mutating jsdom's URL. A handed-in location is never written back.
   */
  readonly location?: { readonly pathname: string; readonly search: string };
}


/**
 * Whether the chip's "Allow this file" may be pressed right now.
 *
 * Two holds, both mount-local and both lifting on their own:
 *
 *  - PENDING — the user clicked and the server has not answered. Lifts when a
 *    newer row for the link arrives (`seq` moves) or after
 *    {@link FIGMA_ALLOW_RETRY_MS}, so a frame the server dropped cannot leave
 *    the button dead forever.
 *  - REFUSED — the server refused a click a second try could fix. Lifts after
 *    {@link FIGMA_ALLOW_RETRY_MS}, counted from when this page saw the refusal:
 *    always after the server's own cooldown started, so the click this offers is
 *    never one it throttles. A remount restarts the wait, which is the safe
 *    direction.
 */
const useAllowGate = (
  link: FigmaLinkSnapshot,
): { readonly enabled: boolean; readonly press: () => void } => {
  const [pendingSeq, setPendingSeq] = useState<number | null>(null);
  const [refusalHeld, setRefusalHeld] = useState(
    link.allowRefusal !== undefined,
  );
  useEffect(() => {
    if (link.allowRefusal === undefined) {
      setRefusalHeld(false);
      return;
    }
    setRefusalHeld(true);
    const id = setTimeout(() => setRefusalHeld(false), FIGMA_ALLOW_RETRY_MS);
    return () => clearTimeout(id);
  }, [link.allowRefusal, link.seq]);
  useEffect(() => {
    if (pendingSeq === null) return;
    if (link.seq !== pendingSeq) {
      setPendingSeq(null);
      return;
    }
    const id = setTimeout(() => setPendingSeq(null), FIGMA_ALLOW_RETRY_MS);
    return () => clearTimeout(id);
  }, [link.seq, pendingSeq]);
  const press = useCallback(() => setPendingSeq(link.seq), [link.seq]);
  return { enabled: pendingSeq === null && !refusalHeld, press };
};

/**
 * Seconds left until `retryAtIso`, or the relative `seconds` when the row
 * carries no instant (an older server). `null` when there is nothing to count.
 */
const retrySecondsLeft = (
  seconds: number | undefined,
  retryAtIso: string | undefined,
  nowMs: number,
): number | null => {
  if (retryAtIso !== undefined) {
    const at = Date.parse(retryAtIso);
    if (Number.isFinite(at)) return Math.max(0, Math.ceil((at - nowMs) / 1000));
  }
  return seconds ?? null;
};

/**
 * A visible countdown for `rate_limited`, ticking from the server's own
 * `Retry-After`.
 *
 * Counts to the server-stamped INSTANT (`retryAtIso`) when the row has one.
 * Replay is full, so the chip remounts on every reconnect — a countdown that
 * started from `retryAfterS` each time would show "try again in 30 s" an hour
 * after the 429. Without an instant it falls back to counting `retryAfterS`
 * down from mount.
 *
 * Keyed on `seq` as well: two consecutive 429s can carry the SAME
 * `Retry-After`, and restarting on a value change alone would freeze the second
 * one at whatever the first had counted down to. `seq` is the bus sequence of
 * the event that wrote the row, so it moves on every attempt.
 *
 * Returns `null` when there is nothing to count. `0` means the wait is over —
 * and nothing retries on its own, so the chip stops counting and says to ask
 * again.
 */
const useRetryCountdown = (
  seconds: number | undefined,
  retryAtIso: string | undefined,
  seq: number,
): number | null => {
  const [left, setLeft] = useState<number | null>(() =>
    retrySecondsLeft(seconds, retryAtIso, Date.now()),
  );
  useEffect(() => {
    const first = retrySecondsLeft(seconds, retryAtIso, Date.now());
    setLeft(first);
    if (first === null || first <= 0) return;
    const anchored =
      retryAtIso !== undefined && Number.isFinite(Date.parse(retryAtIso));
    const id = setInterval(() => {
      setLeft((prev) => {
        const next = anchored
          ? (retrySecondsLeft(seconds, retryAtIso, Date.now()) ?? 0)
          : prev === null || prev <= 0
            ? 0
            : prev - 1;
        if (next <= 0) clearInterval(id);
        return next;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [seconds, retryAtIso, seq]);
  return left;
};

/** The label a chip wears (`figmaChipTitle`). `label` and `frameName` are the
 *  designer's own words and ride the wire capped and sanitised; their absence
 *  is ordinary, so the fallback names the link's place in its message, or the
 *  KIND — never an error.
 *
 *  Two ordinary ways they are absent, and the fallback has to read right for
 *  both. One: Loom has not opened the design yet, so nobody knows its name.
 *  Two: this chip came back from a REPLAY seeded off the durable log, which
 *  keeps the row's status, age, counts and limit countdown but not the three
 *  names (`redactEventForPersistence`, server-side) — so a session that
 *  outlived its Cloud Run instance shows a chip that is still correct about
 *  everything except what the design is called. */
const useChipLabel = (link: FigmaLinkSnapshot): string => {
  const t = useT();
  const num = useFormatNumber();
  const title = figmaChipTitle(link);
  if ("text" in title) return title.text;
  return t(title.key, {
    index: num(title.index ?? 0),
    count: num(title.count ?? 0),
  });
};

/** A `{when}` shorter than this renders as "now", which is not a time to ask
 *  again at — the line then drops the time instead. */
const REFRESH_WHEN_MIN_MS = 60_000;

/** `setTimeout`'s ceiling (~24.8 days). A later instant re-arms on firing. */
const MAX_TIMER_MS = 2_147_483_647;

/**
 * Render again once `atMs` has passed, so a line about an instant stops being
 * true ON TIME rather than on whatever render happens next. `NaN` arms nothing.
 */
const useRerenderAt = (atMs: number): void => {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!Number.isFinite(atMs)) return;
    const wait = atMs - Date.now();
    if (wait < 0) return;
    const id = setTimeout(
      () => setTick((n) => n + 1),
      Math.min(wait + 1, MAX_TIMER_MS),
    );
    return () => clearTimeout(id);
  }, [atMs, tick]);
};

/**
 * One quiet line under a `read` chip whose latest re-read was refused: why, and
 * for a limit that named an instant, when to ask again. The facts above it
 * stay — they are what the read that worked learnt.
 */
const ChipRefreshLine = ({ link }: ChipMetaProps) => {
  const t = useT();
  const rel = useRelativeTime();
  const { locale } = useLocale();
  const copy = figmaRefreshCopy(link);
  const iso = link.refreshRefusal?.retryAtIso;
  const at = iso !== undefined ? Date.parse(iso) : Number.NaN;
  useRerenderAt(copy?.overKey !== undefined ? at : Number.NaN);
  if (copy === null) return null;
  const now = Date.now();
  const known = Number.isFinite(at);
  const text =
    copy.overKey !== undefined && known && at <= now
      ? t(copy.overKey)
      : copy.untilKey !== null && known && at - now >= REFRESH_WHEN_MIN_MS
        ? t(copy.untilKey, {
            when:
              copy.untilAs === "instant"
                ? formatFigmaResetInstant(locale, at, now)
                : rel(at),
          })
        : t(copy.key);
  return <span className="fig-meta fig-refresh">{text}</span>;
};

interface ChipMetaProps {
  readonly link: FigmaLinkSnapshot;
}

/**
 * The facts under a `read` chip: where the file lives, how big it is, how many
 * comments came with it, when it last changed.
 *
 * Every count renders as `Label: n`, which is plural-neutral in both locales —
 * `plurals.ts` is owned elsewhere, and "1 Rahmen"/"2 Rahmen" through a
 * hand-rolled ternary is exactly the English-only grammar that file exists to
 * delete. The timestamp goes through `useRelativeTime` and the numbers through
 * `useFormatNumber` for the same reason: never a hand-built English string.
 */
const ChipMeta = ({ link }: ChipMetaProps) => {
  const t = useT();
  const num = useFormatNumber();
  const rel = useRelativeTime();
  const parts: string[] = [];
  if (link.folderName !== undefined && link.folderName.trim() !== "") {
    parts.push(`${t("focus.figma.meta.folder")}: ${link.folderName}`);
  }
  // Every node in the frame is a layer in Figma's own words; frames and text
  // layers are the two kinds of it named beside the total.
  if (link.counts?.nodes !== undefined) {
    parts.push(`${t("focus.figma.state.metaLayers")}: ${num(link.counts.nodes)}`);
  }
  if (link.counts?.frames !== undefined) {
    parts.push(`${t("focus.figma.meta.frames")}: ${num(link.counts.frames)}`);
  }
  if (link.counts?.textNodes !== undefined) {
    parts.push(`${t("focus.figma.meta.texts")}: ${num(link.counts.textNodes)}`);
  }
  // Comments are a first-class input, not a footnote: a designer's annotation on
  // a frame IS the intent that belongs in the issue, so the count is named even
  // when it is zero — "Comments: 0" is a fact the user can act on, and its
  // absence would read as "Loom didn't look".
  if (link.commentCount !== undefined) {
    parts.push(`${t("focus.figma.meta.comments")}: ${num(link.commentCount)}`);
  }
  if (link.lastModifiedIso !== undefined) {
    parts.push(`${t("focus.figma.meta.updated")}: ${rel(link.lastModifiedIso)}`);
  }
  // Where the design came from, when it did not come from Figma just now.
  //
  // One more part on this line rather than a chip state of its own: the state
  // genuinely IS `read` — the design reached the model — so a `read_from_store`
  // member would demand its own tone and remedy in `FIGMA_STATE_COPY` and make
  // the chip argue with itself. But it cannot be silent either: without a
  // signal, a copy served while Figma is blocked looks exactly like a fresh
  // read, which is the same "the user cannot tell what happened" failure this
  // panel exists to end, wearing the opposite face.
  if (link.fromStore !== undefined) {
    parts.push(
      `${t("focus.figma.meta.fromStore")} · ${t("focus.figma.meta.storeReadAt", {
        when: rel(link.fromStore.readAtIso),
      })}`,
    );
  }
  if (parts.length === 0) return null;
  return <span className="fig-meta">{parts.join(" · ")}</span>;
};

interface ChipProps {
  readonly link: FigmaLinkSnapshot;
  readonly copy: FigmaChipCopy;
  readonly connectHref: string;
  readonly onAllowFile: (linkId: string) => void;
  readonly allowPreview: boolean;
  readonly sessionId: string | null;
  readonly sceneFor?: (handle: string) => DesignScene | null;
}

const FigmaChip = ({
  link,
  copy,
  connectHref,
  onAllowFile,
  allowPreview,
  sessionId,
  sceneFor,
}: ChipProps) => {
  const t = useT();
  const num = useFormatNumber();
  const countdown = useRetryCountdown(
    link.state === "rate_limited" ? link.retryAfterS : undefined,
    link.state === "rate_limited" ? link.retryAtIso : undefined,
    link.seq,
  );
  const label = useChipLabel(link);
  const allowGate = useAllowGate(link);
  const seconds = countdown ?? link.retryAfterS ?? 0;
  // A wait that has run out is not "ask again in 0 s": nothing retries on its
  // own, so the chip drops the number and says to ask again.
  const titleKey =
    link.state === "rate_limited" && countdown === 0
      ? "focus.figma.state.rateLimitedSoon"
      : copy.titleKey;
  // A long wait as minutes / hours, a long block as a date (`limitCopy.ts`).
  const limitLine = useFigmaLimitLine(link, countdown);
  // Whether the sketch is open, per viewer and mount-local. It starts CLOSED
  // and stays local on purpose: a scrollback with four Figma chips must not
  // become four unrequested drawings, and re-opening after a reconnect wipes
  // the timeline is a glance, not a loss. Collapsing is the safe direction —
  // the same reasoning `FigmaDeclinedChip` uses for its own expansion.
  const [sketchOpen, setSketchOpen] = useState(false);
  const canSketch = allowPreview && link.state === "read" && sessionId !== null;

  return (
    <div className={`fig-chip is-${copy.tone}`} data-state={link.state}>
      <span className="fig-chip-head">
        <span className="fig-dot" aria-hidden="true" />
        <span className="fig-name">{label}</span>
      </span>
      {/* The one live region on the surface, and it wraps the STATUS only. A
          chip that flips loading → read while the user is mid-sentence must
          announce the change without reading the composer back to them. */}
      <span className="fig-line" role="status" aria-live="polite">
        {limitLine ?? t(titleKey, { seconds: num(seconds) })}
      </span>
      {copy.bodyKey !== null && (
        <span className="fig-body">{t(copy.bodyKey)}</span>
      )}
      {link.state === "read" && <ChipMeta link={link} />}
      {link.state === "read" && <ChipRefreshLine link={link} />}
      {copy.action === "allow" && (
        <button
          type="button"
          className="focus-btn is-ghost fig-act"
          disabled={!allowGate.enabled}
          onClick={() => {
            // The button is disabled while held; this guard covers the click
            // that lands in the same frame as the re-render.
            if (!allowGate.enabled) return;
            allowGate.press();
            onAllowFile(link.linkId);
          }}
        >
          {t("focus.figma.allowFile")}
        </button>
      )}
      {/* The chat mode of the sketch: no question, no gate, nothing to answer —
          just a picture of what Loom read, on request. It reuses the same act
          slot "Allow this file" uses, and needs no new frame and no new server
          branch, because the drawing is a plain GET. */}
      {canSketch && (
        <button
          type="button"
          className="focus-btn is-ghost fig-act"
          aria-expanded={sketchOpen}
          onClick={() => setSketchOpen((open) => !open)}
        >
          {t(
            sketchOpen
              ? "focus.figma.preview.hide"
              : "focus.figma.preview.show",
          )}
        </button>
      )}
      {canSketch && sketchOpen && (
        <FigmaPreview
          sessionId={sessionId}
          previewId={link.linkId}
          // The chat's OWN copy of the picture, when the replay carried one:
          // the chip's `linkId` is a key into the same `figma_scene` fold the
          // read's `previewId` is. Null falls back to the route, which serves
          // Loom's newest copy of the frame under the same dated caption.
          scene={sceneFor?.(link.linkId) ?? null}
          // From the CHIP's own React state, never from the drawing's payload.
          // That is what lets the payload stay free of layer names while the
          // caption still says which screen this is.
          {...(link.frameName !== undefined ? { frameLabel: link.frameName } : {})}
          maxH={PREVIEW_CHAT_MAX_H}
        />
      )}
      {(copy.action === "connect" || copy.action === "reconnect") && (
        // An `<a>`, not a button: an OAuth round trip is a full-page navigation
        // and cannot be a fetch. Native keyboard behaviour comes free.
        <a className="focus-btn is-ghost fig-act" href={connectHref}>
          {t(
            copy.action === "reconnect"
              ? "focus.figma.reconnect"
              : "focus.figma.connect",
          )}
        </a>
      )}
      {/* The message held more links than Loom looks at in one go. Said once,
          on its last chip, so the left-out design is not simply never
          mentioned. */}
      {(link.inMessage?.skipped ?? 0) > 0 && (
        <span className="fig-meta fig-skipped">
          {t("focus.figma.state.linksSkipped", {
            count: num(link.inMessage?.skipped ?? 0),
          })}
        </span>
      )}
    </div>
  );
};

interface DeclinedChipProps {
  readonly link: FigmaLinkSnapshot;
  readonly connectHref: string;
}

/**
 * A declined link collapses to one grey line with a way back.
 *
 * The expansion is the only piece of this surface the server does not hold, and
 * that is deliberate: the DECLINE is durable (it rides the replay), the
 * re-expansion is a glance. A remount collapsing it again is the safe
 * direction — it can never resurrect a prompt the user dismissed.
 */
const FigmaDeclinedChip = ({
  link,
  connectHref,
}: DeclinedChipProps) => {
  const t = useT();
  const [open, setOpen] = useState(false);
  const label = useChipLabel(link);
  if (!open) {
    return (
      <button
        type="button"
        className="fig-chip is-neutral fig-collapsed"
        onClick={() => setOpen(true)}
      >
        <span className="fig-name">{label}</span>
        <span className="fig-line">{t("focus.figma.state.declined")}</span>
        <span className="fig-expand">{t("focus.figma.showAgain")}</span>
      </button>
    );
  }
  return (
    <div className="fig-chip is-neutral" data-state="declined">
      <span className="fig-chip-head">
        <span className="fig-dot" aria-hidden="true" />
        <span className="fig-name">{label}</span>
      </span>
      <span className="fig-line">{t("focus.figma.state.declinedOpen")}</span>
      <div className="fig-acts">
        <a className="focus-btn fig-act" href={connectHref}>
          {t("focus.figma.connect")}
        </a>
        <button
          type="button"
          className="focus-btn is-ghost fig-act"
          onClick={() => setOpen(false)}
        >
          {t("focus.figma.hideAgain")}
        </button>
      </div>
    </div>
  );
};

export const FigmaLinkPanel = ({
  links,
  onDecline,
  onAllowFile,
  allLinks,
  location,
  allowPreview = false,
  sessionId = null,
  sceneFor,
}: FigmaLinkPanelProps) => {
  const t = useT();
  const live = useHereLocation();
  const here = location ?? live.here;
  const connectHref = useMemo(
    () => figmaConnectHref(figmaReturnToPath(here.pathname, here.search)),
    [here.pathname, here.search],
  );
  // The callback's own outcome. A cancelled or refused connect otherwise leaves
  // NO trace at all: the user is bounced back to a card that looks exactly as it
  // did before they clicked it.
  //
  // LATCHED, then the marker is STRIPPED. Left on the URL, `?figma=oauth_error`
  // outlives the attempt: a reload an hour later, a copied link, a bookmark all
  // show the old error again under a card that may since have worked. So the
  // line is read into state once and the URL is replaced without the marker —
  // other query parameters and the hash kept. A later marker (a second connect
  // attempt) replaces the line; the strip itself does not clear it.
  const [resultKey, setResultKey] = useState<string | null>(() =>
    figmaResultCopyKey(new URLSearchParams(here.search).get(FIGMA_RESULT_PARAM)),
  );
  const replaceUrl = live.replace;
  const overridden = location !== undefined;
  const hash = overridden ? "" : live.here.hash;
  useEffect(() => {
    const params = new URLSearchParams(here.search);
    if (!params.has(FIGMA_RESULT_PARAM)) return;
    setResultKey(figmaResultCopyKey(params.get(FIGMA_RESULT_PARAM)));
    // A handed-in location is a test's, not the address bar's.
    if (overridden) return;
    replaceUrl(`${figmaReturnToPath(here.pathname, here.search)}${hash}`);
  }, [here.pathname, here.search, hash, overridden, replaceUrl]);
  // Decided over the whole conversation, shown only beside its own link. A link
  // pasted in several messages has a chip under each (`placeFigmaLinks`), and
  // the card goes beside the newest of them only: one card, never one per copy.
  const prompt = useMemo(() => {
    const all = allLinks ?? links;
    const decided = figmaConnectPrompt(all);
    if (decided === null) return null;
    const newest = all.find((l) => l.linkId === decided.linkId)?.sourceSeq;
    return links.some(
      (l) =>
        l.linkId === decided.linkId &&
        (newest === undefined ||
          l.sourceSeq === undefined ||
          l.sourceSeq === newest),
    )
      ? decided
      : null;
  }, [allLinks, links]);

  if (links.length === 0) return null;

  return (
    <section className="fig-panel" aria-label={t("focus.figma.panel.regionLabel")}>
      {prompt !== null && (
        <div className="fig-connect">
          {resultKey !== null && (
            <span className="fig-line fig-result" role="status">
              {t(resultKey)}
            </span>
          )}
          <span className="fig-connect-title">
            {t("focus.figma.connectTitle")}
          </span>
          <span className="fig-body">{t("focus.figma.connectBody")}</span>
          {/* The seat sentence belongs HERE, at connect time — not on the
              twentieth read, where it arrives as a wall. Figma exposes no seat,
              no plan and no org on any endpoint Loom may call (`GET /v1/me` is
              id, handle, image, email), so the class is undetectable until a
              429 says so. Saying it up front is the only honest place to say
              it. */}
          <span className="fig-seat">{t("focus.figma.connectSeat")}</span>
          <div className="fig-acts">
            <a className="focus-btn fig-act" href={connectHref}>
              {t("focus.figma.connect")}
            </a>
            <button
              type="button"
              className="focus-btn is-ghost fig-act"
              onClick={() => onDecline(prompt.linkId, "link")}
            >
              {t("focus.figma.later")}
            </button>
            {/* Offered only on the SECOND ask, and there is no third: the first
                "Später" is per link and silent, this one silences the session,
                and after it `figmaConnectPrompt` returns null forever. */}
            {prompt.offerSession && (
              <button
                type="button"
                className="focus-btn is-ghost fig-act"
                onClick={() => onDecline(prompt.linkId, "session")}
              >
                {t("focus.figma.panel.neverAsk")}
              </button>
            )}
          </div>
        </div>
      )}
      <div className="fig-chips">
        {links.map((link) =>
          link.state === "declined" ? (
            <FigmaDeclinedChip
              key={link.linkId}
              link={link}
              connectHref={connectHref}
            />
          ) : (
            <FigmaChip
              key={link.linkId}
              link={link}
              // A `not_connected` chip with NO card above it is a dead end: the
              // card is the only place Connect lives, and it is suppressed once
              // the user has silenced the session. Give the chip the act back,
              // so "nicht mehr fragen" silences the PROMPT and never the way in.
              copy={
                prompt === null && link.state === "not_connected"
                  ? { ...figmaChipCopy(link), action: "connect" as const }
                  : figmaChipCopy(link)
              }
              connectHref={connectHref}
              onAllowFile={onAllowFile}
              allowPreview={allowPreview}
              sessionId={sessionId}
              {...(sceneFor !== undefined ? { sceneFor } : {})}
            />
          ),
        )}
      </div>
    </section>
  );
};
